type SearchAnalyticsOnboardingSessionStatus =
  | "DRAFT"
  | "READY"
  | "RUNNING"
  | "PREVIEW_READY"
  | "FAILED"
  | "APPLIED";

export type SearchAnalyticsSetupStatus =
  | "hidden"
  | "not_started"
  | "in_progress"
  | "resumable"
  | "preview_ready_next_action"
  | "completed";

type SetupStatusSession = {
  status: string;
  latestSnapshotID?: string | null;
  projectID?: string | null;
} | null;

type SetupStatusSearchAnalyticsProject = {
  searchAnalytics?: {
    bigQuery?: {
      latestDataDate?: string | null;
    } | null;
  } | null;
};

export type SearchAnalyticsSetupStatusInput = {
  session?: SetupStatusSession;
  searchAnalyticsProjects?: ReadonlyArray<SetupStatusSearchAnalyticsProject>;
};

const PREVIEW_READY_STATUSES = new Set<SearchAnalyticsOnboardingSessionStatus>([
  "PREVIEW_READY",
]);

const IN_PROGRESS_STATUSES = new Set<SearchAnalyticsOnboardingSessionStatus>([
  "DRAFT",
  "READY",
  "RUNNING",
]);

/**
 * Search Analytics 初期分析 session を、sidebar の再開導線と空状態 CTA が共有できる状態語彙へ変換します。
 * 新規開始は SA project 作成導線が担当するため、session がない `not_started` は sidebar 表示対象にしません。
 */
export function deriveSearchAnalyticsSetupStatus({
  session,
  searchAnalyticsProjects = [],
}: SearchAnalyticsSetupStatusInput): SearchAnalyticsSetupStatus {
  const hasUsableSearchAnalyticsProject = searchAnalyticsProjects.some(
    (project) => Boolean(project.searchAnalytics?.bigQuery?.latestDataDate),
  );

  if (!session) {
    return hasUsableSearchAnalyticsProject ? "hidden" : "not_started";
  }

  const sessionStatus =
    session.status as SearchAnalyticsOnboardingSessionStatus;

  if (sessionStatus === "APPLIED") {
    return "completed";
  }

  if (sessionStatus === "FAILED") {
    return "resumable";
  }

  if (PREVIEW_READY_STATUSES.has(sessionStatus) || session.latestSnapshotID) {
    return "preview_ready_next_action";
  }

  if (IN_PROGRESS_STATUSES.has(sessionStatus)) {
    return "in_progress";
  }

  return hasUsableSearchAnalyticsProject ? "hidden" : "not_started";
}

/**
 * Sidebar の初期分析行に「未完了」バッジを出すべき状態を判定します。
 * 初期分析 session が未作成の `not_started` は行自体を表示しないため、badge 対象から外します。
 */
export function shouldShowSearchAnalyticsSetupBadge(
  status: SearchAnalyticsSetupStatus,
) {
  return (
    status === "in_progress" ||
    status === "resumable" ||
    status === "preview_ready_next_action"
  );
}
